import { useCallback, useEffect, useMemo, useState } from "react";
import { getIn, useFormikContext } from "formik";

// @ts-expect-error pure js
import AffectationSelect from "../UI/AffectationSelect";
import {
  useBalanceTypeFamilies,
  useBalanceTypes,
  // @ts-expect-error pure js
} from "../../hooks/use-balances";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
// @ts-expect-error pure js
import usePropertyFolders from "../../hooks/use-property-folders";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
// @ts-expect-error pure js
import useMandataire from "../../hooks/use-mandataire";
import {
  BalanceDomain,
  DOMAIN_TO_PARENT_ID_FIELD_NAME,
  TAX_REGIME_LABELS,
} from "../../utils/constants/balances";
import {
  formatBalanceAffectation,
  getAffectationCandidates,
  getAffectationDomains,
  getAffectationSubtree,
  getBalanceTypeFamilyGroups,
  getBalanceTypeOptionsForSubtree,
  getReachableDomains,
  getSubtreeTaxRegimes,
  resolveAffectation,
  restrictSubtreeToDomains,
  subtreeUses2072,
} from "../../utils/balances";
import BalanceTypeCascade from "./BalanceTypeCascade";
import Button from "../UI/Button";
import Text from "../UI/Text";
import styles from "./BalanceTargetFields.module.css";
import { IconFilter, IconFilterX } from "@tabler/icons-react";

// Sur la page d'un locataire, l'opération courante est un règlement.
const DEFAULT_FAMILY_BY_DOMAIN: Record<string, string> = {
  [BalanceDomain.TENANT]: "tenant_payment",
};

interface BalanceTargetFieldsProps {
  prefix?: string;
  context?: {
    domaine?: string;
    parentId?: number | string;
  };
  isEdit?: boolean;
  excludeCategories?: string[];
  excludeFamilies?: string[];
  restrictToDomains?: string[];
  withFolders?: boolean;
  onAffectationChange?: (domaine: string, parentId: number) => void;
  disabled?: boolean;
}

const NO_EXCLUDED_CATEGORIES: string[] = [];
const NO_EXCLUDED_FAMILIES: string[] = [];

export default function BalanceTargetFields({
  prefix = "",
  context,
  isEdit = false,
  excludeCategories = NO_EXCLUDED_CATEGORIES,
  excludeFamilies = NO_EXCLUDED_FAMILIES,
  restrictToDomains,
  withFolders,
  onAffectationChange,
  disabled = false,
}: BalanceTargetFieldsProps) {
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();
  const { balanceTypes, isLoading } = useBalanceTypes();
  const { families } = useBalanceTypeFamilies();
  const [showAllTypes, setShowAllTypes] = useState(false);
  const { tenants, isLoading: isLoadingTenants } = useTenants();
  const { properties, isLoading: isLoadingProperties } = useProperties();
  const { propertyFolders, isLoading: isLoadingFolders } = usePropertyFolders();
  const { profileBailleurs, isLoading: isLoadingProfiles } =
    useProfileBailleurs();
  const { mandataire } = useMandataire();
  const isAgency = Boolean(mandataire);

  const isLoadingSubtree =
    isLoadingTenants ||
    isLoadingProperties ||
    isLoadingFolders ||
    isLoadingProfiles;

  // Le plafond s'applique au sous-arbre, d'où dérivent aussi bien les groupes de
  // catégories que les options d'affectation : une catégorie proposée a toujours une
  // cible atteignable.
  const subtree = useMemo(
    () =>
      restrictSubtreeToDomains(
        getAffectationSubtree(
          context,
          { properties, propertyFolders, tenants, profileBailleurs },
          { withFolders },
        ),
        restrictToDomains,
      ),
    [
      context,
      properties,
      propertyFolders,
      tenants,
      profileBailleurs,
      withFolders,
      restrictToDomains,
    ],
  );

  const selectedType = getIn(values, `${prefix}type`);
  const reachable = getReachableDomains(subtree);
  const allowedDomains = getAffectationDomains(
    selectedType,
    balanceTypes,
  ).filter((domaine: string) => reachable.includes(domaine));

  const entityCount =
    subtree.profileIds.size +
    subtree.folderIds.size +
    subtree.propertyIds.size +
    subtree.tenantIds.size;
  // Un candidat unique est posé d'office par le couplage ci-dessous : le sélecteur
  // l'affiche sans rien laisser choisir.
  const hasSingleCandidate =
    Boolean(selectedType) &&
    getAffectationCandidates(allowedDomains, subtree).length === 1;

  const contextKey = `${context?.domaine ?? ""}:${context?.parentId ?? ""}`;
  // Les groupes d'identifiants sont séparés : collés bout à bout, deux sous-arbres
  // différents rendraient la même signature.
  const subtreeKey = useMemo(
    () =>
      [
        subtree.profileIds,
        subtree.folderIds,
        subtree.propertyIds,
        subtree.tenantIds,
      ]
        .map((ids: Set<number>) => [...ids].join(","))
        .join("|"),
    [subtree],
  );

  useEffect(() => {
    // Les collections arrivent vides au premier rendu : les lire alors reviendrait à
    // prendre « pas encore chargé » pour « aucun candidat » et à effacer la cible d'une
    // configuration existante.
    if (!selectedType || isLoadingSubtree) return;
    const rowDomaine = getIn(values, `${prefix}domaine`);
    const current = {
      domaine: rowDomaine,
      parentId: getIn(
        values,
        `${prefix}${
          DOMAIN_TO_PARENT_ID_FIELD_NAME[
            rowDomaine as keyof typeof DOMAIN_TO_PARENT_ID_FIELD_NAME
          ]
        }`,
      ),
    };
    const resolved = resolveAffectation(current, allowedDomains, subtree);

    const promises = [];
    if (!resolved?.kept) {
      for (const field of Object.values(DOMAIN_TO_PARENT_ID_FIELD_NAME)) {
        promises.push(setFieldValue(`${prefix}${field}`, ""));
      }
      promises.push(setFieldValue(`${prefix}domaine`, resolved?.domaine ?? ""));
      if (resolved) {
        promises.push(
          setFieldValue(
            `${prefix}${
              DOMAIN_TO_PARENT_ID_FIELD_NAME[
                resolved.domaine as keyof typeof DOMAIN_TO_PARENT_ID_FIELD_NAME
              ]
            }`,
            String(resolved.parentId),
          ),
        );
      }
      // Le rappel suit la cible quelle qu'en soit l'origine : branché sur le seul
      // sélecteur, il laisserait les consommateurs sur la cible précédente chaque fois
      // que le couplage la déplace ou la vide.
      onAffectationChange?.(resolved?.domaine ?? "", resolved?.parentId ?? 0);
    }
    promises.push(
      setFieldValue(
        `${prefix}affectation`,
        resolved
          ? formatBalanceAffectation(resolved.domaine, resolved.parentId)
          : "",
      ),
    );
    Promise.all(promises).catch((error) => console.error(error));
    // Le couplage suit la catégorie, le contexte et le périmètre atteignable, mais
    // ne se rejoue pas au changement d'affectation : le relancer à chaque fois
    // écraserait le choix que l'utilisateur vient de faire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, contextKey, subtreeKey, isLoadingSubtree]);

  // Le régime fiscal des profils du périmètre filtre les types proposés ; tant que les
  // collections chargent, aucun profil n'est connu et rien n'est filtré.
  const subtreeTaxRegimes = useMemo(
    () =>
      isLoadingSubtree
        ? null
        : getSubtreeTaxRegimes(subtree, {
            properties,
            propertyFolders,
            tenants,
            profileBailleurs,
          }),
    [
      isLoadingSubtree,
      subtree,
      properties,
      propertyFolders,
      tenants,
      profileBailleurs,
    ],
  );

  // La SCI à l'IR cite les lignes de la 2072-S dans l'indication fiscale.
  const form2072 = useMemo(
    () =>
      !isLoadingSubtree &&
      subtreeUses2072(subtree, {
        properties,
        propertyFolders,
        tenants,
        profileBailleurs,
      }),
    [
      isLoadingSubtree,
      subtree,
      properties,
      propertyFolders,
      tenants,
      profileBailleurs,
    ],
  );

  const taxRegimes = showAllTypes ? null : subtreeTaxRegimes;
  const filterLabel = subtreeTaxRegimes
    ? `régime ${[...subtreeTaxRegimes]
        .map((regime) => TAX_REGIME_LABELS[regime] ?? regime)
        .join(" et ")}`
    : "";

  const typeFilters = useMemo(
    () => ({
      isEdit,
      excludeCategories,
      excludeFamilies,
      taxRegimes,
      isAgency,
    }),
    [isEdit, excludeCategories, excludeFamilies, taxRegimes, isAgency],
  );

  const familyOptions = useMemo(
    () =>
      getBalanceTypeFamilyGroups(
        subtree,
        { balanceTypes, families },
        typeFilters,
      ),
    [subtree, balanceTypes, families, typeFilters],
  );

  const typeOptionsForFamily = useCallback(
    (family: string) =>
      getBalanceTypeOptionsForSubtree(
        subtree,
        { balanceTypes },
        { ...typeFilters, family },
      ),
    [subtree, balanceTypes, typeFilters],
  );

  if (isLoading) return null;

  return (
    <div className={styles.fields}>
      <BalanceTypeCascade
        prefix={prefix}
        familyOptions={familyOptions}
        typeOptionsForFamily={typeOptionsForFamily}
        defaultFamily={
          context?.domaine
            ? DEFAULT_FAMILY_BY_DOMAIN[context.domaine]
            : undefined
        }
        taxRegimes={taxRegimes}
        form2072={form2072}
        typeFilter={
          filterLabel && (
            <div className={styles.regimeFilter}>
              {!showAllTypes && <Text variant="deci">{filterLabel}</Text>}
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                icon={showAllTypes ? IconFilter : IconFilterX}
                onClick={() => setShowAllTypes((current) => !current)}
              >
                {showAllTypes
                  ? `Filtrer sur ${filterLabel}`
                  : "Voir tous les types"}
              </Button>
            </div>
          )
        }
        disabled={disabled}
      />
      {entityCount > 1 && (
        <AffectationSelect
          prefix={prefix}
          subtree={subtree}
          allowedDomains={allowedDomains}
          resetTypeOnChange={false}
          disabled={disabled || !selectedType || hasSingleCandidate}
          placeholder={
            selectedType ? undefined : "Choisissez d'abord un type d'opération"
          }
          withFolders={withFolders}
          // AffectationSelect obtient domaine et parentId en découpant la valeur de
          // l'option choisie : les deux sortent en chaînes. On convertit ici l'identifiant
          // pour que le rappel transmis reçoive bien le nombre annoncé par son type.
          onChange={
            onAffectationChange
              ? (domaine: string, parentId: string) =>
                  onAffectationChange(domaine, Number(parentId))
              : undefined
          }
          mandatory
        />
      )}
    </div>
  );
}
